import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Circle, Clock, XCircle, Sparkles, Send, MailCheck } from "lucide-react";

/**
 * Sprint 92ag — Member activation drip timeline.
 * Shows the 6-step onboarding sequence status for one member and the profile
 * completeness checklist. Also lets the admin re-send any step now.
 */
const STEP_LABELS = {
  1: { label: "J0 — Bienvenue", desc: "Pôles, WhatsApp, premiers réflexes" },
  2: { label: "J+2 — Complétez votre profil", desc: "Personnel, entreprise, équipe, produits" },
  3: { label: "J+5 — Actualités communauté", desc: "WineTech Monthly, événements, notifications" },
  4: { label: "J+8 — Publier vos actus", desc: "Articles, validation, LinkedIn" },
  5: { label: "J+12 — Développez votre réseau", desc: "Annuaire, messages, Club" },
  6: { label: "J+20 — Relance (conditionnel)", desc: "Envoyée uniquement si profil incomplet" },
};

const fmt = (iso) => iso ? new Date(iso).toLocaleDateString("fr", { day: "2-digit", month: "short", year: "numeric" }) : "";
const fmtTime = (iso) => iso ? new Date(iso).toLocaleString("fr", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "";

export default function MemberActivationTimeline({ email }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState({});
  // Sprint 92ag — Preview send state (persists locally so admin doesn't retype).
  const [previewTo, setPreviewTo] = useState(() => {
    try { return localStorage.getItem("activation_preview_to") || ""; } catch { return ""; }
  });
  const [previewLang, setPreviewLang] = useState("fr");

  const load = () => {
    if (!email) return;
    api.get(`/admin/activation-sequences/${encodeURIComponent(email)}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ sequence: null, completeness: null }));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [email]);

  const start = async () => {
    setBusy({ ...busy, start: true });
    try {
      await api.post(`/admin/activation-sequences/${encodeURIComponent(email)}/start`);
      toast.success("Séquence d'activation démarrée");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setBusy({});
    }
  };

  const sendNow = async (step) => {
    setBusy({ ...busy, [step]: true });
    try {
      await api.post(`/admin/activation-sequences/${encodeURIComponent(email)}/send-now/${step}`);
      toast.success(`Étape ${step} programmée pour envoi immédiat`);
      setTimeout(load, 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setBusy({});
    }
  };

  // Sprint 92ag — Send a template preview (or the full sequence) to any address.
  const sendPreview = async (steps) => {
    const to = (previewTo || "").trim().toLowerCase();
    if (!to.includes("@")) { toast.error("Renseigne un email valide dans le champ ci-dessus"); return; }
    try { localStorage.setItem("activation_preview_to", to); } catch { /* ignore */ }
    const key = Array.isArray(steps) && steps.length === 1 ? `preview-${steps[0]}` : "preview-all";
    setBusy({ ...busy, [key]: true });
    try {
      await api.post(`/admin/activation-sequences/send-preview`, { to_email: to, steps, lang: previewLang });
      toast.success(steps.length === 1 ? `Aperçu de l'étape ${steps[0]} envoyé à ${to}` : `Séquence complète envoyée à ${to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setBusy({});
    }
  };

  if (!email) return null;
  const seq = data?.sequence;
  const comp = data?.completeness;

  return (
    <div className="rounded-2xl border border-neutral-100 bg-white p-6" data-testid="member-activation-timeline">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <h2 className="text-lg font-bold text-navy inline-flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-wine-500" /> Séquence d'activation
        </h2>
        <div className="text-xs text-neutral-500">
          {email}
        </div>
      </div>

      {/* Profile completeness */}
      {comp && (
        <div className="mt-4 rounded-xl border border-neutral-100 bg-neutral-50 p-4" data-testid="activation-completeness">
          <div className="mb-2 flex items-center justify-between gap-2">
            <span className="text-xs font-bold uppercase tracking-wider text-neutral-600">Profil complété</span>
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${comp.complete ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
              {Object.values(comp.checks).filter(Boolean).length}/{Object.keys(comp.checks).length}
              {comp.complete ? " • Complet" : ""}
            </span>
          </div>
          <ul className="grid grid-cols-1 gap-1 sm:grid-cols-2 text-xs">
            {Object.entries(comp.checks).map(([key, ok]) => (
              <li key={key} className="flex items-center gap-2" data-testid={`activation-check-${key}`}>
                {ok ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> : <Circle className="h-3.5 w-3.5 text-neutral-300" />}
                <span className={ok ? "text-neutral-700" : "text-neutral-500"}>{PROFILE_LABEL[key] || key}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Sprint 92ag — Preview send bar: send templates to any address for review. */}
      <div className="mt-4 rounded-xl border border-wine-100 bg-wine-50/40 p-3" data-testid="activation-preview-bar">
        <div className="flex flex-wrap items-center gap-2">
          <MailCheck className="h-4 w-4 text-wine-600" />
          <span className="text-xs font-bold uppercase tracking-wider text-wine-700">Envoyer pour vérification</span>
          <input
            type="email"
            value={previewTo}
            onChange={(e) => setPreviewTo(e.target.value)}
            placeholder="audrey@lawinetech.com"
            className="flex-1 min-w-[220px] rounded-full border border-wine-200 bg-white px-3 py-1.5 text-xs"
            data-testid="activation-preview-email"
          />
          <select
            value={previewLang}
            onChange={(e) => setPreviewLang(e.target.value)}
            className="rounded-full border border-wine-200 bg-white px-2 py-1.5 text-xs font-semibold"
            data-testid="activation-preview-lang"
          >
            <option value="fr">FR</option>
            <option value="en">EN</option>
            <option value="es">ES</option>
            <option value="it">IT</option>
            <option value="pt">PT</option>
          </select>
          <button
            onClick={() => sendPreview([1, 2, 3, 4, 5, 6])}
            disabled={!!busy["preview-all"]}
            className="inline-flex items-center gap-1 rounded-full bg-wine-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-wine-700 disabled:opacity-50"
            data-testid="activation-preview-all"
          >
            <Send className="h-3 w-3" /> {busy["preview-all"] ? "Envoi…" : "Envoyer les 6 étapes"}
          </button>
        </div>
        <p className="mt-1.5 text-[11px] text-neutral-500">
          Envoie une version d'aperçu de chaque email à l'adresse ci-dessus (sujet préfixé « [Aperçu — Étape&nbsp;X] »). N'affecte pas la séquence du membre.
        </p>
      </div>

      {/* Sequence timeline */}
      {!seq && (
        <div className="mt-4 rounded-xl bg-neutral-50 p-4 text-center">
          <p className="text-sm text-neutral-600">Aucune séquence démarrée pour ce membre.</p>
          <button
            onClick={start}
            disabled={busy.start}
            className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-wine-600 px-4 py-1.5 text-xs font-bold text-white hover:bg-wine-700 disabled:opacity-50"
            data-testid="activation-start-btn"
          >
            <Sparkles className="h-3 w-3" /> {busy.start ? "Démarrage…" : "Démarrer la séquence"}
          </button>
        </div>
      )}

      {seq && (
        <ol className="mt-4 space-y-2" data-testid="activation-steps">
          {seq.steps.map((s) => {
            const meta = STEP_LABELS[s.step] || { label: `Étape ${s.step}`, desc: "" };
            const sent = !!s.sent_at;
            const skipped = !!s.skipped;
            const pending = !sent && !skipped;
            return (
              <li key={s.step} className={`flex items-start gap-3 rounded-xl border p-3 ${sent ? "border-emerald-100 bg-emerald-50/40" : skipped ? "border-amber-100 bg-amber-50/40" : "border-neutral-100 bg-white"}`} data-testid={`activation-step-${s.step}`}>
                <div className="shrink-0 pt-0.5">
                  {sent && <CheckCircle2 className="h-5 w-5 text-emerald-600" />}
                  {skipped && <XCircle className="h-5 w-5 text-amber-600" />}
                  {pending && <Clock className="h-5 w-5 text-neutral-400" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                    <span className="text-sm font-bold text-navy">{meta.label}</span>
                    <span className="text-[11px] text-neutral-500">prévu le {fmt(s.planned_for)}</span>
                  </div>
                  <div className="mt-0.5 text-xs text-neutral-600">{meta.desc}</div>
                  {sent && <div className="mt-1 text-[11px] text-emerald-700">Envoyé le {fmtTime(s.sent_at)}</div>}
                  {skipped && <div className="mt-1 text-[11px] text-amber-700">Ignoré — {s.skipped_reason || "condition non remplie"}</div>}
                </div>
                <button
                  onClick={() => sendNow(s.step)}
                  disabled={!!busy[s.step]}
                  className="shrink-0 inline-flex items-center gap-1 rounded-full border border-wine-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-wine-700 hover:bg-wine-50 disabled:opacity-50"
                  title="Renvoyer cette étape maintenant au membre"
                  data-testid={`activation-resend-${s.step}`}
                >
                  <Send className="h-3 w-3" />
                  {busy[s.step] ? "…" : sent ? "Renvoyer" : "Envoyer"}
                </button>
                <button
                  onClick={() => sendPreview([s.step])}
                  disabled={!!busy[`preview-${s.step}`]}
                  className="shrink-0 inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-neutral-700 hover:bg-neutral-50 disabled:opacity-50"
                  title="Envoyer un aperçu de cette étape à l'adresse ci-dessus"
                  data-testid={`activation-preview-${s.step}`}
                >
                  <MailCheck className="h-3 w-3" />
                  {busy[`preview-${s.step}`] ? "…" : "Aperçu"}
                </button>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

const PROFILE_LABEL = {
  personal_photo: "Photo personnelle",
  personal_profile: "Profil personnel (bio / fonction)",
  company_profile: "Description entreprise",
  company_logo: "Logo entreprise",
  main_pole: "Pôle principal",
  products_added: "Au moins 1 produit",
  team_invited: "Au moins 1 collaborateur",
};
